// ✅ THEME-ONLY UPDATE: AdminFeedbackForms -> match the SAME "above theme" (StudentAttendanceByStudent / AdminFeeManagement)
// - Adds pageWrapSx + rootPaperSx (transparent shell)
// - Header uses headerSx (orange gradient + same layout)
// - Body uses bodySx (BG)
// - Filters + cards use sectionSx with LEFT orange stripe + sectionInnerPadSx
// - Inputs use inputSx (focused label orange) + Autocomplete chips/indicators styling (autoCompleteSx)
// - Buttons use primaryBtnSx + outlinedBtnSx (same as report)
// - Switch colors match theme
// - Toasts/alerts rounded + subtle blue border
// ✅ LOGIC UNCHANGED

import React from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_URL } from "./config";
import { FEEDBACK_TYPES, CATEGORIES } from "./FeedbackConfig";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

function getToken() {
  return localStorage.getItem("auth");
}

const emptyDraft = (row) => ({
  FormID: row.FormID,
  CampusCode: row.CampusCode || "",
  SubCampusCode: row.SubCampusCode || "",
  FeedbackType: row.FeedbackType || "general",
  Category: row.Category || "",
  Title: row.Title || "",
  Description: row.Description || "",
  AllowOverallRating: Number(row.AllowOverallRating || 0),
  OverallRatingLabel: row.OverallRatingLabel || "Overall rating",
  IsActive: Number(row.IsActive || 0),
  StartAt: row.StartAt ? dayjs(String(row.StartAt).replace(" ", "T")) : null,
  EndAt: row.EndAt ? dayjs(String(row.EndAt).replace(" ", "T")) : null,
});

// ✅ SQL string for backend
const toSqlDateTime = (d) => {
  if (!d || !dayjs(d).isValid()) return null;
  return dayjs(d).format("YYYY-MM-DD HH:mm:ss");
};

// ✅ For display in view mode (safe)
const fmt = (v) => {
  if (!v) return "-";
  const d = dayjs(String(v).replace(" ", "T"));
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : String(v);
};

export default function AdminFeedbackForms() {
  // ✅ Theme constants (match report pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const sectionInnerPadSx = { pl: 1 };

  // ✅ INPUTS: outline + focus label orange
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  // ✅ Autocomplete styling (chips + indicators like report)
  const autoCompleteSx = {
    ...inputSx,
    "& .MuiAutocomplete-tag": {
      borderRadius: 8,
      backgroundColor: "rgba(25,118,210,0.06)",
      border: "1px solid rgba(25,118,210,0.18)",
      fontWeight: 800,
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
      color: "rgba(25,118,210,0.75)",
      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    whiteSpace: "nowrap",
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    border: "1px solid rgba(25,118,210,0.22)",
    backgroundColor: "rgba(25,118,210,0.04)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: THEME_ORANGE },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: THEME_ORANGE,
      opacity: 0.55,
    },
  };

  const formCardSx = (idx, clickable) => ({
    ...sectionSx,
    p: 2,
    backgroundColor: idx % 2 ? ROW_ALT : "#fff",
    cursor: clickable ? "pointer" : "default",
    "&:hover": { backgroundColor: ROW_HOVER },
  });

  // ===== state / logic (unchanged) =====
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [toast, setToast] = React.useState({ open: false, msg: "", sev: "success" });

  const [editingId, setEditingId] = React.useState(null);
  const [draft, setDraft] = React.useState(null);

  // ===== Filters =====
  const [filters, setFilters] = React.useState({
    FeedbackType: [],
    Category: [],
    IsActive: [], // [1] or [0] or [1,0]
    SubCampusCode: [],
  });

  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const inFilter = (selected = [], value) =>
    !selected?.length || selected.some((s) => norm(s) === norm(value));

  const options = React.useMemo(() => {
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
    return {
      FeedbackType: uniq((rows || []).map((r) => r.FeedbackType)),
      Category: uniq((rows || []).map((r) => r.Category)),
      SubCampusCode: uniq((rows || []).map((r) => r.SubCampusCode)),
      IsActive: [1, 0],
    };
  }, [rows]);

  const filteredRows = React.useMemo(() => {
    return (rows || []).filter((r) => {
      const okType = inFilter(filters.FeedbackType, r.FeedbackType);
      const okCat = inFilter(filters.Category, r.Category);
      const okSub = inFilter(filters.SubCampusCode, r.SubCampusCode);
      const okActive =
        !filters.IsActive.length || filters.IsActive.includes(Number(r.IsActive));
      return okType && okCat && okSub && okActive;
    });
  }, [rows, filters]);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const token = getToken();
      const resp = await axios.get(`${API_URL}/api/feedbackadmin/forms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(resp.data?.rows || resp.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (row) => {
    setEditingId(row.FormID);
    setDraft(emptyDraft(row));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (!draft) return;

    if (!draft.CampusCode.trim()) return setErr("CampusCode is required");
    if (!draft.FeedbackType) return setErr("FeedbackType is required");
    if (!draft.Category) return setErr("Category is required");
    if (!draft.Title.trim()) return setErr("Title is required");

    if (draft.StartAt && draft.EndAt && dayjs(draft.EndAt).isBefore(dayjs(draft.StartAt))) {
      return setErr("EndAt must be after StartAt");
    }

    setErr("");
    try {
      const token = getToken();

      const payload = {
        CampusCode: draft.CampusCode,
        SubCampusCode: draft.SubCampusCode || null,
        FeedbackType: draft.FeedbackType,
        Category: draft.Category,
        Title: draft.Title,
        Description: draft.Description,
        AllowOverallRating: Number(draft.AllowOverallRating ? 1 : 0),
        OverallRatingLabel: draft.OverallRatingLabel,
        IsActive: Number(draft.IsActive ? 1 : 0),
        StartAt: toSqlDateTime(draft.StartAt),
        EndAt: toSqlDateTime(draft.EndAt),
      };

      await axios.put(`${API_URL}/api/feedback/forms/${draft.FormID}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setToast({ open: true, msg: "Form updated successfully", sev: "success" });
      setEditingId(null);
      setDraft(null);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Update failed");
    }
  };

  const categoryOptions = React.useMemo(() => {
    const t = draft?.FeedbackType || "general";
    return CATEGORIES?.[t] || [];
  }, [draft?.FeedbackType]);

  React.useEffect(() => {
    if (!draft) return;
    const opts = categoryOptions;
    if (!opts.length) return;
    if (!opts.includes(draft.Category)) {
      setDraft((p) => ({ ...p, Category: opts[0] || "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.FeedbackType]);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Header bar (exact) */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Feedback Forms
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter, edit, and manage feedback forms
            </Typography>
          </Box>

          <Button
            startIcon={<RefreshIcon />}
            variant="contained"
            onClick={load}
            disabled={loading}
            sx={{
              ...primaryBtnSx,
              backgroundColor: "rgba(255,255,255,0.18)",
              boxShadow: "none",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.25)", boxShadow: "none" },
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* ✅ Body */}
        <Box sx={bodySx}>
          {err ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
              {err}
            </Alert>
          ) : null}

          {/* Filters (section stripe) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                    Filters
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Narrow down forms by type, category, campus and status
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  onClick={() =>
                    setFilters({ FeedbackType: [], Category: [], IsActive: [], SubCampusCode: [] })
                  }
                  sx={outlinedBtnSx}
                >
                  Clear Filters
                </Button>
              </Box>

              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                }}
              >
                <Autocomplete
                  multiple
                  options={options.FeedbackType}
                  value={filters.FeedbackType}
                  onChange={(_, value) => setFilters((p) => ({ ...p, FeedbackType: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField {...params} label="FeedbackType(s)" size="small" sx={inputSx} />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.Category}
                  value={filters.Category}
                  onChange={(_, value) => setFilters((p) => ({ ...p, Category: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField {...params} label="Category(s)" size="small" sx={inputSx} />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField {...params} label="SubCampusCode(s)" size="small" sx={inputSx} />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.IsActive}
                  value={filters.IsActive}
                  onChange={(_, value) => setFilters((p) => ({ ...p, IsActive: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  sx={autoCompleteSx}
                  getOptionLabel={(o) => (Number(o) === 1 ? "Active" : "Not Active")}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" size="small" sx={inputSx} />
                  )}
                />

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography sx={{ fontSize: "0.85rem", opacity: 0.8 }}>Loading…</Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "0.85rem", opacity: 0.8 }}>
                      Showing <b>{filteredRows.length}</b> of <b>{rows.length}</b>
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

          {/* Forms list */}
          {loading ? (
            <Paper variant="outlined" sx={{ ...sectionSx, p: 3 }}>
              <Box sx={{ pl: 1, display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {filteredRows.map((row, idx) => {
                const isEditing = editingId === row.FormID;

                return (
                  <Grid item xs={12} key={row.FormID}>
                    <Paper variant="outlined" sx={formCardSx(idx, !isEditing)}>
                      <Box sx={sectionInnerPadSx}>
                        {/* Header row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                            {row.CampusCode} {row.SubCampusCode ? `• ${row.SubCampusCode}` : ""}
                          </Typography>

                          {!isEditing ? (
                            <IconButton sx={iconBtnSx} onClick={() => startEdit(row)}>
                              <EditIcon />
                            </IconButton>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton sx={iconBtnSx} onClick={saveEdit}>
                                <SaveIcon />
                              </IconButton>
                              <IconButton sx={iconBtnSx} onClick={cancelEdit}>
                                <CancelIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>

                        {/* View mode */}
                        {!isEditing ? (
                          <Box
                            sx={{
                              mt: 1,
                              display: "grid",
                              gridTemplateColumns: "repeat(12, 1fr)",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                              <Typography variant="body2">
                                <b>FeedbackType:</b> {row.FeedbackType}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                              <Typography variant="body2">
                                <b>Category:</b> {row.Category}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
                              <Typography variant="body2">
                                <b>Title:</b> {row.Title}
                              </Typography>
                            </Box>

                            <Box sx={{ gridColumn: "span 12" }}>
                              <Typography variant="body2">
                                <b>Description:</b> {row.Description || "-"}
                              </Typography>
                            </Box>

                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                              <Typography variant="body2">
                                <b>AllowOverallRating:</b> {row.AllowOverallRating === 1 ? "Yes" : "No"}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                              <Typography variant="body2">
                                <b>OverallRatingLabel:</b> {row.OverallRatingLabel}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 2" } }}>
                              <Typography variant="body2">
                                <b>Status:</b> {row.IsActive === 1 ? "Active" : "Not Active"}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 2" } }}>
                              <Typography variant="body2">
                                <b>StartAt:</b> {fmt(row.StartAt)}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: "span 12", md: "span 2" } }}>
                              <Typography variant="body2">
                                <b>EndAt:</b> {fmt(row.EndAt)}
                              </Typography>
                            </Box>

                            <Box sx={{ gridColumn: "span 12" }}>
                              <Typography variant="caption" color="text.secondary">
                                CreatedBy: {row.CreatedByUser} • CreatedAt: {fmt(row.CreatedAt)} • UpdatedAt: {fmt(row.UpdatedAt)}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          // EDIT MODE
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="CampusCode"
                                  value={draft.CampusCode}
                                  onChange={(e) => setDraft((p) => ({ ...p, CampusCode: e.target.value }))}
                                  size="small"
                                  sx={inputSx}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="SubCampusCode"
                                  value={draft.SubCampusCode}
                                  onChange={(e) => setDraft((p) => ({ ...p, SubCampusCode: e.target.value }))}
                                  size="small"
                                  sx={inputSx}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  select
                                  label="FeedbackType"
                                  value={draft.FeedbackType}
                                  onChange={(e) => setDraft((p) => ({ ...p, FeedbackType: e.target.value }))}
                                  size="small"
                                  sx={inputSx}
                                >
                                  {FEEDBACK_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                      {t.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  select
                                  label="Category"
                                  value={draft.Category}
                                  onChange={(e) => setDraft((p) => ({ ...p, Category: e.target.value }))}
                                  disabled={!categoryOptions.length}
                                  size="small"
                                  sx={inputSx}
                                >
                                  {categoryOptions.map((c) => (
                                    <MenuItem key={c} value={c}>
                                      {c}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label="Title"
                                  value={draft.Title}
                                  onChange={(e) => setDraft((p) => ({ ...p, Title: e.target.value }))}
                                  size="small"
                                  sx={inputSx}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label="OverallRatingLabel"
                                  value={draft.OverallRatingLabel}
                                  onChange={(e) => setDraft((p) => ({ ...p, OverallRatingLabel: e.target.value }))}
                                  disabled={!draft.AllowOverallRating}
                                  size="small"
                                  sx={inputSx}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  label="Description"
                                  value={draft.Description}
                                  onChange={(e) => setDraft((p) => ({ ...p, Description: e.target.value }))}
                                  size="small"
                                  sx={inputSx}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!draft.AllowOverallRating}
                                      onChange={(e) =>
                                        setDraft((p) => ({
                                          ...p,
                                          AllowOverallRating: e.target.checked ? 1 : 0,
                                        }))
                                      }
                                      sx={switchSx}
                                    />
                                  }
                                  label="AllowOverallRating"
                                  sx={{ "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!draft.IsActive}
                                      onChange={(e) => setDraft((p) => ({ ...p, IsActive: e.target.checked ? 1 : 0 }))}
                                      sx={switchSx}
                                    />
                                  }
                                  label="IsActive"
                                  sx={{ "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <DateTimePicker
                                  label="StartAt"
                                  value={draft.StartAt}
                                  onChange={(newValue) => setDraft((p) => ({ ...p, StartAt: newValue }))}
                                  slotProps={{
                                    textField: { fullWidth: true, size: "small", sx: inputSx },
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <DateTimePicker
                                  label="EndAt"
                                  value={draft.EndAt}
                                  onChange={(newValue) => setDraft((p) => ({ ...p, EndAt: newValue }))}
                                  slotProps={{
                                    textField: { fullWidth: true, size: "small", sx: inputSx },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </LocalizationProvider>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          <Snackbar
            open={toast.open}
            autoHideDuration={2500}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.sev}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
              sx={{ borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
}
